import { logger } from './logger';
import api from './api';

/**
 * Structured field error from backend validation
 */
export interface CVFieldError {
  field: string;           // Full path e.g., "publications[0].year"
  section: string;         // e.g., "publications"
  index: number | null;    // Index in array, null for non-array fields
  fieldName: string;       // e.g., "year"
  message: string;         // Human-readable error message
}

/**
 * Custom error class for CV validation errors with structured field info
 */
export class CVValidationError extends Error {
  fieldErrors: CVFieldError[];

  constructor(message: string, fieldErrors: CVFieldError[]) {
    super(message);
    this.name = 'CVValidationError';
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Parse FastAPI validation error response into structured field errors
 *
 * FastAPI 422 responses have this structure:
 * {
 *   "detail": [{
 *     "loc": ["body", "publications", 0, "year"],
 *     "msg": "Value error, Year must be a valid format..."
 *   }]
 * }
 */
function parseValidationErrors(data: unknown): CVFieldError[] {
  const errors: CVFieldError[] = [];

  if (!data || typeof data !== 'object') {
    return errors;
  }

  const response = data as { detail?: Array<{ loc: Array<string | number>; msg: string }> };

  if (!response.detail || !Array.isArray(response.detail)) {
    return errors;
  }

  for (const error of response.detail) {
    if (!error.loc || !Array.isArray(error.loc)) {
      continue;
    }

    // loc format: ["body", "section", index, "field"] or ["body", "field"]
    // Skip the "body" prefix
    const loc = error.loc.slice(1);

    if (loc.length === 0) {
      continue;
    }

    let section: string;
    let index: number | null = null;
    let fieldName: string;

    if (loc.length === 1) {
      // Top-level field: ["biography"]
      section = String(loc[0]);
      fieldName = section;
    } else if (loc.length === 2 && typeof loc[1] === 'number') {
      // Array item without nested field: ["publications", 0]
      section = String(loc[0]);
      index = loc[1];
      fieldName = section;
    } else if (loc.length >= 3) {
      // Array item with nested field: ["publications", 0, "year"]
      section = String(loc[0]);
      index = typeof loc[1] === 'number' ? loc[1] : null;
      fieldName = String(loc[loc.length - 1]);
    } else {
      // Two string elements: ["section", "field"]
      section = String(loc[0]);
      fieldName = String(loc[1]);
    }

    // Build field path string
    const field = index !== null
      ? `${section}[${index}].${fieldName}`
      : fieldName;

    // Clean up the error message (remove "Value error, " prefix if present)
    const message = error.msg.replace(/^Value error,?\s*/i, '');

    errors.push({
      field,
      section,
      index,
      fieldName,
      message
    });
  }

  return errors;
}

export interface CVData {
  biography?: string;
  socialMedia?: Array<{ type: string; link: string }>;
  education?: Array<{ text: string }>;
  soloExhibitions?: Array<{ text: string }>;
  groupExhibitions?: Array<{ text: string }>;
  publications?: Array<{ text: string }>;
  awards?: Array<{ text: string }>;
  contact?: Array<{ type: string; value: string }>;
  collections?: Array<{ name: string }>;
  galleryRepresentation?: Array<{ name: string }>;
  additionalInfo?: string;
  entryPrivacy?: { [key: string]: boolean };
  /**
   * Asset existence flags populated by the backend via S3 head_object checks.
   * When false/absent the frontend skips the speculative asset-URL fetch,
   * preventing browser-console 404 errors for users who have no uploaded assets.
   */
  hasProfileImage?: boolean;
  hasCvPdf?: boolean;
  hasLogo?: boolean;
}

/**
 * Build the query-param object for CV endpoints.
 *
 * `owner_id` and `catalogAliId` are orthogonal axes — both can be sent
 * together when a delegate scopes a delegator's CV to a specific owned
 * catalog. Backend resolution: `owner_id` picks the user (delegation),
 * `catalogAliId` picks one of THAT user's owned catalogs. When neither
 * is sent the backend defaults to the caller's primary.
 */
const buildCvParams = (
  ownerId?: string | null,
  catalogAliId?: string | null,
): Record<string, string> | undefined => {
  const params: Record<string, string> = {};
  if (ownerId) params.owner_id = ownerId;
  if (catalogAliId) params.catalogAliId = catalogAliId;
  return Object.keys(params).length > 0 ? params : undefined;
};

export const getCVData = async (
  ownerId?: string | null,
  catalogAliId?: string | null,
): Promise<CVData> => {
  try {
    const params = buildCvParams(ownerId, catalogAliId);
    const response = await api.get('/api/cv', params ? { params } : undefined);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return {};
    }
    logger.error('[CV API] Failed to fetch CV data', error);
    throw error;
  }
};

export const saveCVData = async (
  data: CVData,
  ownerId?: string | null,
  catalogAliId?: string | null,
): Promise<void> => {
  try {
    const params = buildCvParams(ownerId, catalogAliId);
    await api.post('/api/cv', data, params ? { params } : undefined);
  } catch (error) {
    logger.error('[CV API] Failed to save CV data', error);
    if (error.response) {
      logger.error('[CV API] Error response', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });

      // Parse 422 validation errors into structured format
      if (error.response.status === 422) {
        const fieldErrors = parseValidationErrors(error.response.data);
        if (fieldErrors.length > 0) {
          const errorMessages = fieldErrors.map(e => `${e.section}[${e.index}].${e.fieldName}: ${e.message}`);
          throw new CVValidationError(
            `Validation failed: ${errorMessages.join('; ')}`,
            fieldErrors
          );
        }
      }
    }
    throw error;
  }
};

/**
 * Public CV data returned from the public endpoint.
 * Contains user info and privacy-filtered CV sections.
 */
export interface PublicCVData {
  userId: string;
  aliId: string;
  name?: string;
  profileImageUrl?: string;
  cvPdfUrl?: string;
  logoUrl?: string;
  biography?: string;
  additionalInfo?: string;
  socialMedia?: Array<{ type: string; link: string }>;
  education?: Array<{ text: string }>;
  soloExhibitions?: Array<{ text: string }>;
  groupExhibitions?: Array<{ text: string }>;
  publications?: Array<{ text: string }>;
  awards?: Array<{ text: string }>;
  contact?: Array<{ type: string; value: string }>;
  collections?: Array<{ name: string }>;
  galleryRepresentation?: Array<{ name: string }>;
}

/**
 * Fetch public CV data for a user by their ALI ID.
 * Returns privacy-filtered CV data (only sections marked as public).
 *
 * @param aliId - The user's ALI ID (e.g., "ALI-12345")
 * @returns Public CV data with user info and filtered sections
 * @throws Error if user or CV not found (404) or other API errors
 */
export const getPublicCVData = async (aliId: string): Promise<PublicCVData> => {
  try {
    const response = await api.get(`/api/cv/public/${aliId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('CV not found');
    }
    logger.error('[CV API] Failed to fetch public CV data', error);
    throw error;
  }
};
